import {
  DEFAULT_WAKE_RETRY_AFTER_SEC,
  isJobAdmissionKillSwitchActive,
  isWakeAppDeferredResult,
  type WakeAppDeferredResult,
  type WakeAppResult,
} from "@gencow/core";
import {
  resolveScheduledDueMaxClaimsPerAppPerPoll,
  resolveScheduledMaxRunningPerApp,
} from "./job-admission-policy.js";
import { getCurrentPlatformNodeId } from "./platform-active-slot.js";
import { extractWorkflowResumeArgs } from "./workflow-resume-args.js";

type LoggerLike = Pick<Console, "log" | "warn" | "error">;

type DueScheduledJobRow = {
  id: string;
  app_id: number;
  action: string;
  args: string;
  on_error_action: string | null;
  retry_count: number;
  max_retries: number;
};

type ScheduledAppRow = {
  id: number;
  name: string;
  port: number;
  status: string;
};

type AppIdentityRow = {
  id: number;
  name: string;
};

const WORKFLOW_RESUME_ACTION_PREFIX = "__gencow.workflow.resume.";
const WORKFLOW_RESUME_MAX_CONCURRENT_PER_APP = 8;
const WORKFLOW_RESUME_MAX_CONCURRENT_PER_ACTION = 3;
export const APP_MEMORY_EXCEEDED_STATUS = "memory_exceeded";
export const SCHEDULED_MEMORY_EXCEEDED_ERROR = "blocked_memory_exceeded";
export const SCHEDULED_MEMORY_EXCEEDED_DEFER_SECONDS = 300;
const WORKFLOW_ID_FROM_ARGS_SQL = `
  CASE
    WHEN jsonb_typeof(args) = 'object' THEN args->>'workflowId'
    WHEN jsonb_typeof(args) = 'string'
      AND (args #>> '{}') ~ '^\\s*\\{'
      THEN ((args #>> '{}')::jsonb)->>'workflowId'
    ELSE NULL
  END
`;

export interface ProcessScheduledJobsPollOptions {
  rawSql: (query: string, params?: unknown[]) => Promise<unknown>;
  getPort: (appName: string) => number | undefined | null;
  wakeApp: (appName: string) => Promise<WakeAppResult>;
  provisionApp?: (
    appName: string,
    port: number,
    dataDir: string,
    appDbId: number,
    isProd: boolean,
    reason: "wake",
  ) => Promise<void>;
  setAppMeta?: (appName: string, meta: { port: number; dataDir: string; appDbId: number }) => void;
  generateCronToken: (appId: number, appName: string) => string;
  cronExecTimeoutMs: number;
  maxJobsPerPoll?: number;
  workflowResumeMaxConcurrentPerApp?: number;
  workflowResumeMaxConcurrentPerAction?: number;
  scheduledDueMaxClaimsPerAppPerPoll?: number;
  scheduledMaxRunningPerApp?: number;
  env?: Record<string, string | undefined>;
  fetchImpl?: typeof fetch;
  logger?: LoggerLike;
}

export interface ProcessScheduledJobsPollResult {
  claimed: number;
  executed: number;
  deferredDueLimited?: number;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function getRunningJobStaleAfterSec(cronExecTimeoutMs: number): number {
  return Math.max(60, Math.ceil(cronExecTimeoutMs / 1000) + 30);
}

function getWorkflowResumeDeliveryStaleAfterSec(): number {
  return 45;
}

function getPositiveIntegerOption(value: number | undefined, fallback: number): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) return fallback;
  return value;
}

function parseScheduledJobArgs(args: string): unknown {
  try {
    return JSON.parse(args || "{}");
  } catch {
    return {};
  }
}

function getWorkflowResumeId(action: string, args: unknown): string | null {
  if (!action.startsWith(WORKFLOW_RESUME_ACTION_PREFIX)) return null;
  return extractWorkflowResumeArgs(args).workflowId ?? null;
}

async function deleteCompletedScheduledJob(
  options: ProcessScheduledJobsPollOptions,
  job: DueScheduledJobRow,
  workflowId: string | null,
): Promise<void> {
  if (!workflowId) {
    await options.rawSql(`DELETE FROM scheduled_jobs WHERE id = $1`, [job.id]);
    return;
  }

  await options.rawSql(
    `
      DELETE FROM scheduled_jobs
      WHERE app_id = $1
        AND action = $2
        AND (
          id = $3
          OR ${WORKFLOW_ID_FROM_ARGS_SQL} = $4
        )
    `,
    [job.app_id, job.action, job.id, workflowId],
  );
}

async function handleScheduledJobFailure(
  options: ProcessScheduledJobsPollOptions,
  job: DueScheduledJobRow,
  appName: string,
  errorMsg: string,
): Promise<void> {
  const logger = options.logger ?? console;
  const fetchImpl = options.fetchImpl ?? fetch;
  const nextRetry = job.retry_count + 1;

  if (nextRetry < job.max_retries) {
    const backoffSec = 10 * Math.pow(3, nextRetry);
    await options.rawSql(
      `UPDATE scheduled_jobs
             SET status = 'pending',
                 retry_count = $1,
                 error = $2,
                 run_at = NOW() + make_interval(secs => $3),
                 updated_at = NOW()
             WHERE id = $4`,
      [nextRetry, errorMsg, backoffSec, job.id],
    );
    logger.warn(
      `[scheduled] ${appName}/${job.action} (${job.id}): retry ${nextRetry}/${job.max_retries} in ${backoffSec}s — ${errorMsg}`,
    );
    return;
  }

  await options.rawSql(
    `UPDATE scheduled_jobs
         SET status = 'failed',
             error = $1,
             retry_count = $2
         WHERE id = $3`,
    [errorMsg, nextRetry, job.id],
  );
  logger.error(
    `[scheduled] ${appName}/${job.action} (${job.id}): FAILED (${nextRetry} retries exhausted) — ${errorMsg}`,
  );

  if (!job.on_error_action) return;

  try {
    const port = options.getPort(appName);
    if (!port) return;

    const appRows = (await options.rawSql(`SELECT id, name FROM apps WHERE name = $1`, [
      appName,
    ])) as AppIdentityRow[];
    const appRow = appRows[0];
    if (!appRow) return;

    const cronToken = options.generateCronToken(appRow.id, appRow.name);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), options.cronExecTimeoutMs);

    try {
      await fetchImpl(`http://localhost:${port}/internal/cron/${job.on_error_action}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Cron-Token": cronToken,
        },
        body: JSON.stringify({
          action: job.on_error_action,
          args: { failedAction: job.action, failedJobId: job.id, error: errorMsg },
        }),
        signal: controller.signal,
      });
      logger.log(`[scheduled] ${appName}/${job.on_error_action}: onError handler invoked for ${job.action}`);
    } finally {
      clearTimeout(timeout);
    }
  } catch (error) {
    logger.error(
      `[scheduled] ${appName}/${job.on_error_action}: onError handler failed:`,
      errorMessage(error),
    );
  }
}

async function deferScheduledJobForWake(
  options: ProcessScheduledJobsPollOptions,
  job: DueScheduledJobRow,
  appName: string,
  result?: WakeAppDeferredResult,
): Promise<void> {
  const retryAfterSec = result?.retryAfterSec || DEFAULT_WAKE_RETRY_AFTER_SEC;
  await options.rawSql(
    `UPDATE scheduled_jobs SET status = 'pending', run_at = NOW() + make_interval(secs => $1) WHERE id = $2`,
    [retryAfterSec, job.id],
  );
  const reason = result ? result.status : "boot_failed";
  (options.logger ?? console).warn(`[scheduled] ${appName}/${job.action}: wake deferred (${reason})`);
}

async function deferScheduledJobForMemoryExceeded(
  options: ProcessScheduledJobsPollOptions,
  job: DueScheduledJobRow,
  appName: string,
): Promise<void> {
  await options.rawSql(
    `UPDATE scheduled_jobs
        SET status = 'pending',
            error = $1,
            run_at = NOW() + make_interval(secs => $2),
            updated_at = NOW()
      WHERE id = $3`,
    [SCHEDULED_MEMORY_EXCEEDED_ERROR, SCHEDULED_MEMORY_EXCEEDED_DEFER_SECONDS, job.id],
  );
  (options.logger ?? console).warn(
    `[scheduled] ${appName}/${job.action}: blocked by memory_exceeded app status`,
  );
}

export async function deferMemoryExceededScheduledJobs(options: {
  rawSql: ProcessScheduledJobsPollOptions["rawSql"];
  retryAfterSec?: number;
  nodeId?: string | null;
  logger?: LoggerLike;
}): Promise<number> {
  const retryAfterSec = getPositiveIntegerOption(
    options.retryAfterSec,
    SCHEDULED_MEMORY_EXCEEDED_DEFER_SECONDS,
  );
  const nodeHomeClause = options.nodeId ? "AND a.node_id = $4" : "";
  const queryParams = options.nodeId
    ? [SCHEDULED_MEMORY_EXCEEDED_ERROR, retryAfterSec, APP_MEMORY_EXCEEDED_STATUS, options.nodeId]
    : [SCHEDULED_MEMORY_EXCEEDED_ERROR, retryAfterSec, APP_MEMORY_EXCEEDED_STATUS];
  const rows = (await options.rawSql(
    `UPDATE scheduled_jobs sj
        SET status = 'pending',
            error = $1,
            run_at = NOW() + make_interval(secs => $2),
            updated_at = NOW()
       FROM apps a
      WHERE a.id = sj.app_id
        AND sj.status = 'pending'
        AND sj.run_at <= NOW()
        AND a.status = $3
        ${nodeHomeClause}
      RETURNING TRUE AS blocked`,
    queryParams,
  )) as Array<{ blocked: boolean }>;
  if (rows.length > 0) {
    options.logger?.warn(`[scheduled] ${rows.length} due job(s) blocked by memory_exceeded app status`);
  }
  return rows.length;
}

async function tryDbBasedScheduledProvision(
  options: ProcessScheduledJobsPollOptions,
  appRow: ScheduledAppRow,
): Promise<number | null> {
  const logger = options.logger ?? console;
  if (!options.provisionApp || !options.setAppMeta) return null;

  try {
    const rows = (await options.rawSql(
      `SELECT id, port FROM apps WHERE name = $1 AND status IN ('running','idle')`,
      [appRow.name],
    )) as Array<{ id: number; port: number }>;
    const row = rows[0];
    if (!row) return null;

    const dataDir = `/var/gencow/deployments/${appRow.name}`;
    options.setAppMeta(appRow.name, { port: row.port, dataDir, appDbId: row.id });
    await options.provisionApp(appRow.name, row.port, dataDir, row.id, true, "wake");
    logger.log(`[scheduled] ${appRow.name}: DB-based provision success`);
    return options.getPort(appRow.name) ?? row.port;
  } catch (error) {
    logger.error(`[scheduled] ${appRow.name}: DB-based provision failed:`, errorMessage(error));
    return null;
  }
}

async function executeClaimedScheduledJob(
  options: ProcessScheduledJobsPollOptions,
  job: DueScheduledJobRow,
): Promise<boolean> {
  const logger = options.logger ?? console;
  const fetchImpl = options.fetchImpl ?? fetch;

  const appRows = (await options.rawSql(`SELECT id, name, port, status FROM apps WHERE id = $1`, [
    job.app_id,
  ])) as ScheduledAppRow[];
  const appRow = appRows[0];
  if (!appRow) {
    await options.rawSql(
      `UPDATE scheduled_jobs SET status = 'failed', error = 'App not found' WHERE id = $1`,
      [job.id],
    );
    return false;
  }
  if (appRow.status === APP_MEMORY_EXCEEDED_STATUS) {
    await deferScheduledJobForMemoryExceeded(options, job, appRow.name);
    return false;
  }

  try {
    let port = options.getPort(appRow.name);
    if (!port || appRow.status === "idle") {
      const wakeResult = await options.wakeApp(appRow.name);
      if (!wakeResult.ok) {
        if (!isWakeAppDeferredResult(wakeResult)) {
          logger.warn(`[scheduled] ${appRow.name}: wakeApp failed — trying DB-based provision...`);
          port = await tryDbBasedScheduledProvision(options, appRow);
          if (!port) {
            await deferScheduledJobForWake(options, job, appRow.name);
            return false;
          }
        } else {
          await deferScheduledJobForWake(options, job, appRow.name, wakeResult);
          return false;
        }
      } else {
        port = options.getPort(appRow.name) ?? wakeResult.port;
      }
    }

    if (!port) {
      await deferScheduledJobForWake(options, job, appRow.name);
      return false;
    }

    const cronToken = options.generateCronToken(appRow.id, appRow.name);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), options.cronExecTimeoutMs);
    const parsedArgs = parseScheduledJobArgs(job.args);
    const workflowId = getWorkflowResumeId(job.action, parsedArgs);

    try {
      const res = await fetchImpl(`http://localhost:${port}/internal/cron/${job.action}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Cron-Token": cronToken,
        },
        body: JSON.stringify({ action: job.action, args: parsedArgs }),
        signal: controller.signal,
      });
      clearTimeout(timeout);

      if (res.ok) {
        await deleteCompletedScheduledJob(options, job, workflowId);
        logger.log(`[scheduled] ${appRow.name}/${job.action} (${job.id}): completed ✓`);
        return true;
      }

      const body = await res.text().catch(() => "");
      await handleScheduledJobFailure(options, job, appRow.name, body.slice(0, 500));
      return false;
    } catch (error) {
      clearTimeout(timeout);
      const msg =
        error instanceof Error && error.name === "AbortError"
          ? `Timeout after ${options.cronExecTimeoutMs / 1000}s`
          : errorMessage(error);
      await handleScheduledJobFailure(options, job, appRow.name, msg.slice(0, 500));
      return false;
    }
  } catch (error) {
    const msg = errorMessage(error).slice(0, 500);
    logger.error(`[scheduled] ${appRow.name}/${job.action}: unexpected error:`, msg);
    await options
      .rawSql(`UPDATE scheduled_jobs SET status = 'failed', error = $1 WHERE id = $2`, [msg, job.id])
      .catch(() => {});
    return false;
  }
}

export async function processScheduledJobsPoll(
  options: ProcessScheduledJobsPollOptions,
): Promise<ProcessScheduledJobsPollResult> {
  const logger = options.logger ?? console;
  const maxJobsPerPoll = options.maxJobsPerPoll ?? 20;
  const workflowResumeMaxConcurrentPerApp = getPositiveIntegerOption(
    options.workflowResumeMaxConcurrentPerApp,
    WORKFLOW_RESUME_MAX_CONCURRENT_PER_APP,
  );
  const workflowResumeMaxConcurrentPerAction = getPositiveIntegerOption(
    options.workflowResumeMaxConcurrentPerAction,
    WORKFLOW_RESUME_MAX_CONCURRENT_PER_ACTION,
  );
  const env = options.env ?? process.env;
  const scheduledDueMaxClaimsPerAppPerPoll = resolveScheduledDueMaxClaimsPerAppPerPoll(
    env,
    options.scheduledDueMaxClaimsPerAppPerPoll,
  );
  const scheduledMaxRunningPerApp = resolveScheduledMaxRunningPerApp(env, options.scheduledMaxRunningPerApp);
  const runningJobStaleAfterSec = getRunningJobStaleAfterSec(options.cronExecTimeoutMs);
  const workflowResumeDeliveryStaleAfterSec = getWorkflowResumeDeliveryStaleAfterSec();
  const currentNodeId = getCurrentPlatformNodeId(env);

  try {
    if (isJobAdmissionKillSwitchActive(env, "scheduled:claim")) {
      logger.warn("[scheduled] Poll paused by job admission kill switch");
      return { claimed: 0, executed: 0 };
    }

    await options
      .rawSql(
        `
            UPDATE scheduled_jobs
            SET status = 'pending',
                run_at = NOW() + INTERVAL '5 seconds',
                updated_at = NOW()
            WHERE status = 'running'
              AND action LIKE '${WORKFLOW_RESUME_ACTION_PREFIX}%'
              AND updated_at < NOW() - make_interval(secs => $1)
        `,
        [workflowResumeDeliveryStaleAfterSec],
      )
      .catch(() => {});

    await options
      .rawSql(
        `
            UPDATE scheduled_jobs
            SET status = 'pending',
                retry_count = retry_count + 1,
                run_at = NOW() + INTERVAL '10 seconds',
                updated_at = NOW()
            WHERE status = 'running'
              AND updated_at < NOW() - make_interval(secs => $1)
              AND retry_count < max_retries
        `,
        [runningJobStaleAfterSec],
      )
      .catch(() => {});

    await options
      .rawSql(
        `
            DELETE FROM scheduled_jobs
            WHERE (status = 'failed' AND created_at < NOW() - INTERVAL '7 days')
               OR (
                 status = 'running'
                 AND retry_count >= max_retries
                 AND updated_at < NOW() - make_interval(secs => $1)
               )
        `,
        [runningJobStaleAfterSec],
      )
      .catch(() => {});

    void options
      .rawSql(`DELETE FROM cron_job_runs WHERE started_at < NOW() - INTERVAL '7 days'`)
      .catch(() => {});

    await deferMemoryExceededScheduledJobs({
      rawSql: options.rawSql,
      nodeId: currentNodeId,
      logger,
    }).catch(() => {});

    let claimed = 0;
    let executed = 0;
    let deferredDueLimited = 0;
    const executions: Array<() => Promise<boolean>> = [];
    const claimsByApp = new Map<number, number>();
    const dueLimitedAppIds = new Set<number>();

    for (let i = 0; i < maxJobsPerPoll; i += 1) {
      const excludedAppIds = Array.from(dueLimitedAppIds).filter(Number.isFinite);
      const excludedAppClause =
        excludedAppIds.length > 0 ? `AND sj2.app_id NOT IN (${excludedAppIds.join(", ")})` : "";
      const nodeHomeClause = currentNodeId ? "AND a.node_id = $1" : "";
      const dueJobs = (await options.rawSql(
        `
            UPDATE scheduled_jobs sj
            SET status = 'running', updated_at = NOW()
            FROM (
                SELECT sj2.id
                FROM scheduled_jobs sj2
                JOIN apps a ON a.id = sj2.app_id
                WHERE sj2.status = 'pending'
                  AND sj2.run_at <= NOW()
                  AND a.status IN ('running', 'idle')
                  ${nodeHomeClause}
                  ${excludedAppClause}
                  AND (
                    SELECT COUNT(*)
                    FROM scheduled_jobs running
                    WHERE running.app_id = sj2.app_id
                      AND running.status = 'running'
                  ) < ${scheduledMaxRunningPerApp}
                  AND NOT (
                    sj2.action LIKE '${WORKFLOW_RESUME_ACTION_PREFIX}%'
                    AND (
                      SELECT COUNT(*)
                      FROM scheduled_jobs running
                      WHERE running.app_id = sj2.app_id
                        AND running.action LIKE '${WORKFLOW_RESUME_ACTION_PREFIX}%'
                        AND running.status = 'running'
                    ) >= ${workflowResumeMaxConcurrentPerApp}
                  )
                  AND NOT (
                    sj2.action LIKE '${WORKFLOW_RESUME_ACTION_PREFIX}%'
                    AND (
                      SELECT COUNT(*)
                      FROM scheduled_jobs running
                      WHERE running.app_id = sj2.app_id
                        AND running.action = sj2.action
                        AND running.status = 'running'
                    ) >= ${workflowResumeMaxConcurrentPerAction}
                  )
                ORDER BY sj2.run_at ASC
                LIMIT 1
                FOR UPDATE OF sj2 SKIP LOCKED
            ) sub
            WHERE sj.id = sub.id
            RETURNING sj.id, sj.app_id, sj.action, sj.args::text, sj.on_error_action, sj.retry_count, sj.max_retries
        `,
        currentNodeId ? [currentNodeId] : undefined,
      )) as DueScheduledJobRow[];
      const job = dueJobs?.[0];
      if (!job) break;
      claimed += 1;
      const nextClaimCount = (claimsByApp.get(job.app_id) ?? 0) + 1;
      claimsByApp.set(job.app_id, nextClaimCount);
      if (nextClaimCount >= scheduledDueMaxClaimsPerAppPerPoll) {
        dueLimitedAppIds.add(job.app_id);
      }
      executions.push(() => executeClaimedScheduledJob(options, job));
    }

    if (dueLimitedAppIds.size > 0) {
      const appIds = Array.from(dueLimitedAppIds).filter(Number.isFinite);
      if (appIds.length > 0) {
        const dueLimitedRows = (await options.rawSql(
          `SELECT COUNT(*)::int AS c
             FROM scheduled_jobs
             WHERE status = 'pending'
               AND run_at <= NOW()
               AND app_id IN (${appIds.join(", ")})`,
        )) as Array<{ c: number }>;
        deferredDueLimited = Number(dueLimitedRows[0]?.c ?? 0);
      }
    }

    if (executions.length > 0) {
      const results = await Promise.all(executions.map((execute) => execute()));
      executed = results.filter(Boolean).length;
    }

    if (executed > 0) {
      logger.log(`[scheduled] Poll — ${executed} job(s) executed`);
    }

    return deferredDueLimited > 0 ? { claimed, executed, deferredDueLimited } : { claimed, executed };
  } catch (error) {
    logger.error(`[scheduled] Poller error:`, errorMessage(error));
    return { claimed: 0, executed: 0 };
  }
}
